// Shared business logic for all simulators
// Single source of truth for calculations and constants

// ═══ RÉGIE CALCULATION ═══
export function getRegies(mode, precarite, surface, userCommissions = null) {
  // If user has custom commissions, use them
  if (userCommissions) {
    if (mode === "lead" && userCommissions.lead) return userCommissions.lead;
    if (mode === "regie") {
      if (precarite === "jaune" && userCommissions.regie?.jaune) return userCommissions.regie.jaune;
      const sizeKey = surface < 70 ? "petit" : surface <= 130 ? "moyen" : "grand";
      if (userCommissions.regie?.bleu?.[sizeKey]) return userCommissions.regie.bleu[sizeKey];
    }
  }
  // Default fallback
  if (mode === "lead") return { r174: 2500, rMPR: 2500, r174Cum: 1250, rMPRCum: 1250 };
  if (precarite === "jaune") return { r174: 2500, rMPR: 2500, r174Cum: 1250, rMPRCum: 1250 };
  if (surface < 70) return { r174: 1000, rMPR: 2000, r174Cum: 1000, rMPRCum: 2000 };
  if (surface <= 130) return { r174: 1500, rMPR: 2500, r174Cum: 1500, rMPRCum: 2500 };
  return { r174: 2000, rMPR: 3000, r174Cum: 2000, rMPRCum: 3000 };
}

// ═══ CONSTANTES COMMUNES ═══
export const MONTANT_KWHC = {
  H1: { petit: 327600, moyen: 458640, grand: 655200 },
  H2: { petit: 273000, moyen: 382200, grand: 546000 },
  H3: { petit: 191100, moyen: 267540, grand: 382200 }
};

export const PRIX_RACHAT = { bleu: 0.0125, jaune: 0.0074 };

export const MPR_AIDES = {
  PAC: { bleu: 5000, jaune: 4000 },
  "PAC/SSC": { bleu: 15000, jaune: 12000 },
  "PAC + CESI": { bleu: 9000, jaune: 7000 }
};

export const TAUX_FINANCEMENT_MPR = 0.12;

export const MARGE_TABLE = {
  PAC: {
    bleu: { H1: { petit: 440.85, moyen: 1993.46, grand: 4322.37 }, H2: { petit: -206.07, moyen: 1087.77, grand: 3028.53 }, H3: { petit: -1176.45, moyen: -270.76, grand: 1087.77 } },
    jaune: { H1: { petit: -1970.67, moyen: -1051.53, grand: 327.18 }, H2: { petit: -2353.65, moyen: -1587.70, grand: -438.77 }, H3: { petit: -2928.11, moyen: -2391.95, grand: -1587.70 } }
  },
  "PAC/SSC": {
    bleu: { H1: { petit: 2939.53, moyen: 4492.13, grand: 6821.04 }, H2: { petit: 2292.60, moyen: 3586.44, grand: 5527.20 }, H3: { petit: 1322.22, moyen: 2227.91, grand: 3586.44 } },
    jaune: { H1: { petit: -1127.73, moyen: -208.59, grand: 1170.12 }, H2: { petit: -1510.71, moyen: -744.76, grand: 404.17 }, H3: { petit: -2085.17, moyen: -1549.01, grand: -744.76 } }
  },
  "PAC + CESI": {
    bleu: { H1: { petit: 1690.19, moyen: 3242.79, grand: 5571.70 }, H2: { petit: 1043.26, moyen: 2337.10, grand: 4277.86 }, H3: { petit: 72.88, moyen: 978.57, grand: 2337.10 } },
    jaune: { H1: { petit: -2377.07, moyen: -1457.93, grand: -79.22 }, H2: { petit: -2760.05, moyen: -1994.10, grand: -845.17 }, H3: { petit: -3334.51, moyen: -2798.35, grand: -1994.10 } }
  }
};

export const COEFFICIENTS_SURFACE = [
  { min: 0, max: 34, coef: 0.40 }, { min: 35, max: 59, coef: 0.50 },
  { min: 60, max: 89, coef: 0.80 }, { min: 90, max: 109, coef: 1.00 },
  { min: 110, max: 129, coef: 1.20 }, { min: 130, max: 99999, coef: 1.30 }
];

export const CEE_PAR_CLASSES = { 2: { mwhCumac: 723 }, 3: { mwhCumac: 892 }, 4: { mwhCumac: 1138 } };
export const TVA = 0.055;

export const PERIODES = [
  { id: "before_june_2025", label: "Avant juin 2025" },
  { id: "june_2025_jan_2026", label: "Juin 2025 - Janvier 2026" },
  { id: "from_jan_2026", label: "A partir de janvier 2026" }
];

export const PRECARITE_COLORS = {
  bleu: { bg: "bg-blue-500", text: "text-blue-700", label: "Bleu (Tres Modeste)", eligible: true },
  jaune: { bg: "bg-yellow-400", text: "text-yellow-700", label: "Jaune (Modeste)", eligible: true },
  violet: { bg: "bg-purple-500", text: "text-purple-700", label: "Violet (Intermediaire)", eligible: false },
  rose: { bg: "bg-pink-400", text: "text-pink-700", label: "Rose (Superieur)", eligible: false }
};

export const DPE_CLASSES = ["A", "B", "C", "D", "E", "F", "G"];
export const DPE_COLORS = { A: "bg-green-600", B: "bg-lime-500", C: "bg-yellow-400", D: "bg-amber-400", E: "bg-orange-500", F: "bg-red-500", G: "bg-red-700" };

// ═══ FONCTIONS UTILITAIRES ═══
export function getCoefficient(surface) {
  return (COEFFICIENTS_SURFACE.find(c => surface >= c.min && surface <= c.max) || {}).coef || 1.30;
}

export function getMPRSurfaceKey(surface) {
  if (surface < 70) return "petit";
  if (surface < 90) return "moyen";
  return "grand";
}

export function surfaceToNum(key) {
  if (key === "S < 70") return 60;
  if (key === "70 <= S < 90") return 80;
  return 100;
}

export const formatCurrency = (v) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0
}).format(v || 0);

// ═══ REVENUS & PRECARITE (Bareme officiel 2026) ═══
export const REVENUE_THRESHOLDS = {
  idf: [
    { p: 1, bleu: 24031, jaune: 29253, violet: 40851 },
    { p: 2, bleu: 35270, jaune: 42933, violet: 60051 },
    { p: 3, bleu: 42357, jaune: 51564, violet: 71846 },
    { p: 4, bleu: 49455, jaune: 60208, violet: 84562 },
    { p: 5, bleu: 56580, jaune: 68877, violet: 96817 },
  ],
  province: [
    { p: 1, bleu: 17363, jaune: 22259, violet: 31185 },
    { p: 2, bleu: 25393, jaune: 32553, violet: 45842 },
    { p: 3, bleu: 30540, jaune: 39148, violet: 55196 },
    { p: 4, bleu: 35676, jaune: 45735, violet: 64550 },
    { p: 5, bleu: 40835, jaune: 52348, violet: 73907 },
  ],
  suppIdf: { bleu: 7116, jaune: 8663, violet: 12257 },
  suppProvince: { bleu: 5151, jaune: 6598, violet: 9357 }
};

export function determinePrecarite(nbPersonnes, region, revenu) {
  if (!nbPersonnes || !region || revenu === null || revenu === undefined || revenu === "") return null;
  const r = typeof revenu === 'string' ? parseFloat(String(revenu).replace(/\u20ac/g, '').replace(/\s+/g, '').replace(/,/g, '.')) : revenu;
  const n = parseInt(nbPersonnes);
  if (!Number.isFinite(r) || !Number.isInteger(n) || n < 1 || r < 0) return null;

  const isIdf = String(region).toLowerCase().includes("idf") || String(region).toLowerCase().includes("ile");
  const table = isIdf ? REVENUE_THRESHOLDS.idf : REVENUE_THRESHOLDS.province;
  const supp = isIdf ? REVENUE_THRESHOLDS.suppIdf : REVENUE_THRESHOLDS.suppProvince;

  let sBleu, sJaune, sViolet;
  if (n <= 5) {
    const row = table.find(x => x.p === n);
    if (!row) return null;
    sBleu = row.bleu; sJaune = row.jaune; sViolet = row.violet;
  } else {
    const base = table.find(x => x.p === 5);
    const extra = n - 5;
    sBleu = base.bleu + extra * supp.bleu;
    sJaune = base.jaune + extra * supp.jaune;
    sViolet = base.violet + extra * supp.violet;
  }
  if (r <= sBleu) return "bleu";
  if (r <= sJaune) return "jaune";
  if (r <= sViolet) return "violet";
  return "rose";
}

// ═══ ELIGIBILITE ═══
export function check174Eligibility(profil, periode, dpeActuel, ageLogement) {
  if (!profil || !periode || !dpeActuel || !ageLogement)
    return { eligible: false, reason: "Informations manquantes pour verifier l'eligibilite 174." };
  if (profil === "bailleur_sci") return { eligible: true, reason: "Bailleur SCI : toujours eligible a la 174." };
  if (profil === "proprietaire_occupant") {
    if (periode === "before_june_2025") return { eligible: false, reason: "Proprietaire occupant non eligible a la 174 avant juin 2025." };
    if (periode === "june_2025_jan_2026") {
      if (ageLogement === "2_to_15") return { eligible: true, reason: "Eligible : logement 2-15 ans, periode juin 2025 - jan 2026." };
      return { eligible: false, reason: "Non eligible : logement hors tranche 2-15 ans pour cette periode." };
    }
    if (ageLogement === "less_than_2") return { eligible: false, reason: "Non eligible : logement de moins de 2 ans." };
    if (ageLogement === "2_to_15") return { eligible: true, reason: "Eligible : logement 2-15 ans, a partir de janvier 2026." };
    if (ageLogement === "more_than_15") {
      if (["A", "B", "C", "D"].includes(dpeActuel)) return { eligible: true, reason: `Eligible : logement +15 ans, DPE ${dpeActuel} (A a D).` };
      return { eligible: false, reason: `Non eligible : logement +15 ans mais DPE ${dpeActuel} (necessite A a D).` };
    }
    return { eligible: false, reason: "Donnees insuffisantes." };
  }
  return { eligible: false, reason: "Profil non reconnu." };
}

// ═══ CALCULS MPR ═══
export function calcMPR(zone, surface, precarite, typeChantier, periodeMPRAvant, regieMPR) {
  const surfKey = getMPRSurfaceKey(surface);
  const kwhc = MONTANT_KWHC[zone]?.[surfKey] || 0;
  const prix = PRIX_RACHAT[precarite] || 0;
  const ceeBase = Math.round(kwhc * prix * 1.055);
  const mpr = MPR_AIDES[typeChantier]?.[precarite] || 0;
  const baseMarge = MARGE_TABLE[typeChantier]?.[precarite]?.[zone]?.[surfKey] || 0;
  const margeApresJan = baseMarge + 2500 - regieMPR;
  let marge, complementCEE = 0;
  if (periodeMPRAvant) {
    const targetMarge = precarite === "bleu" ? 6821 : 3321;
    complementCEE = Math.round((targetMarge - margeApresJan) * 1.055);
    marge = targetMarge;
  } else {
    marge = Math.round(margeApresJan * 100) / 100;
  }
  const cee = ceeBase + complementCEE;
  const totalAides = mpr + cee;
  return { mpr, ceeBase, complementCEE, cee, totalAides, marge: Math.round(marge * 100) / 100, kwhc, prixRachat: prix, surfKey, typeChantier };
}

// ═══ CONSTANTES 174 ═══
export const GESTES_ISO = ["combles", "planchersBas", "rampant", "murs", "fenetres"];
export const GESTES_ISO_MAISON_ONLY = ["combles", "planchersBas", "rampant"];
export const GESTES_ISO_LABELS = {
  combles: "Isolation des combles",
  planchersBas: "Isolation du plancher bas",
  rampant: "Rampants",
  murs: "Isolation des murs interieurs",
  fenetres: "Fenetres"
};

export const COUT_UNITAIRE = {
  combles:      { label: "Isolation combles",       materiel: 12, pose: 8,   unite: "m\u00B2" },
  planchersBas: { label: "Isolation plancher bas",  materiel: 12, pose: 10,  unite: "m\u00B2" },
  rampant:      { label: "Rampant",                 materiel: 0,  pose: 50,  unite: "m\u00B2" },
  murs:         { label: "Isolation murs interieurs", materiel: 0, pose: 50, unite: "m\u00B2" },
  fenetres:     { label: "Fenetres",                materiel: 450, pose: 0,  unite: "unite" },
  monoSplit:    { label: "Mono Split",              materiel: 800, pose: 500, unite: "unite" },
  radiateurs:   { label: "Radiateurs electriques",  materiel: 100, pose: 70, unite: "unite" },
  vmcSimple:    { label: "VMC Simple flux",         materiel: 0,  pose: 600, unite: "unite" },
  coutsFixes:   { label: "Couts fixes",             materiel: 0,  pose: 545, unite: "unite" },
};

// ═══ CALCULS 174 ═══
export function calcAutoQuantites174(surface, typeBatiment, niveaux, gestesDeja, chauffage) {
  const isMaison = typeBatiment === "maison";
  const available = {};
  GESTES_ISO.forEach(g => {
    if (gestesDeja.includes(g)) { available[g] = 0; return; }
    if (!isMaison && GESTES_ISO_MAISON_ONLY.includes(g)) { available[g] = 0; return; }
    switch (g) {
      case "combles": available[g] = Math.round(surface * 0.5); break;
      case "planchersBas": available[g] = Math.round(surface * 0.5); break;
      case "rampant": available[g] = Math.round(surface * 0.5); break;
      case "murs": available[g] = Math.round(niveaux === "plain_pied" ? surface / 3 : surface / 2.7); break;
      case "fenetres": available[g] = Math.round(surface * 0.18 * 0.25); break;
      default: available[g] = 0;
    }
  });
  const monoSplit = isMaison && !gestesDeja.includes("monoSplit") ? 1 : 0;
  const radiateurs = (chauffage !== "Gaz" && chauffage !== "Fioul") ? Math.round(surface / 14) : 0;
  const vmcSimple = 1;
  return { ...available, monoSplit, radiateurs, vmcSimple };
}

export function getScenarios174(availableGestes, isMaison, chauffage) {
  const isoAvailable = GESTES_ISO.filter(g => availableGestes[g] > 0);
  if (isoAvailable.length < 2) return [];
  const scenarios = [];
  scenarios.push({ label: "2 gestes d'isolation", sauts: 2, gestes: isoAvailable.slice(0, 2), equipements: [] });
  if (isMaison && availableGestes.monoSplit > 0) {
    scenarios.push({ label: "2 gestes iso + Mono Split", sauts: 4, gestes: isoAvailable.slice(0, 2), equipements: ["monoSplit"] });
  }
  if (availableGestes.radiateurs > 0) {
    scenarios.push({ label: "2 gestes iso + Radiateurs elec.", sauts: 3, gestes: isoAvailable.slice(0, 2), equipements: ["radiateurs"] });
  }
  if (availableGestes.vmcSimple > 0) {
    scenarios.push({ label: "2 gestes iso + VMC Simple flux", sauts: 3, gestes: isoAvailable.slice(0, 2), equipements: ["vmcSimple"] });
  }
  return scenarios.sort((a, b) => b.sauts - a.sauts);
}

export function calcCout174(scenario, availableQtes, couts, regie) {
  let total = 0;
  scenario.gestes.forEach(g => { total += availableQtes[g] * (couts[g]?.materiel + couts[g]?.pose || 0); });
  scenario.equipements.forEach(e => { total += availableQtes[e] * (couts[e]?.materiel + couts[e]?.pose || 0); });
  total += (couts.coutsFixes?.materiel + couts.coutsFixes?.pose || 0);
  total += regie;
  return Math.round(total * 100) / 100;
}

export function calc174(surface, scenario, valorisation, couts, availableQtes, regie, isJaune) {
  const coef = getCoefficient(surface);
  const cee = CEE_PAR_CLASSES[scenario.sauts] || CEE_PAR_CLASSES[2];
  const caTTCBrut = cee.mwhCumac * coef * valorisation;
  const caTTC = caTTCBrut;
  const caHT = caTTC / (1 + TVA);
  const coutTravaux = calcCout174(scenario, availableQtes, couts, regie);
  const marge = caHT - coutTravaux;
  return { caTTCBrut: Math.round(caTTCBrut * 100) / 100, caTTC: Math.round(caTTC * 100) / 100, caHT: Math.round(caHT * 100) / 100, coutTravaux, marge: Math.round(marge * 100) / 100, sauts: scenario.sauts, coef, mwhCumac: cee.mwhCumac, isJaune };
}

// ═══ CHARGES MPR PAR DEFAUT ═══
export const CHARGES_MPR_DEFAUT = {
  "PAC": { marchandise: 3000, sousTraitant: 2000, admin: 80 },
  "PAC/SSC": { marchandise: 6580, sousTraitant: 4200, admin: 80 },
  "PAC + CESI": { marchandise: 4917, sousTraitant: 3917, admin: 80 }
};

// ═══ ETAPES TIMELINE ═══
export const MPR_TIMELINE_STEPS = [
  { label: "Rdv commercial", offset: 0 },
  { label: "Rdv admin", offset: 2 },
  { label: "Signature devis", offset: 0 },
  { label: "Ouverture compte MPR", offset: 0 },
  { label: "Octroi", offset: 10 },
  { label: "RDV Travaux", offset: 4 },
  { label: "Paiement", offset: 7 },
];

export const TIMELINE_174_STEPS = [
  { label: "Rdv commercial", offset: 0 },
  { label: "Rdv admin", offset: 2 },
  { label: "Audit avant travaux", offset: 4 },
  { label: "Signature Devis", offset: 2 },
  { label: "Rdv travaux", offset: 5 },
  { label: "Fin de Travaux", offset: 2 },
  { label: "COFRAC apres Travaux", offset: 10 },
  { label: "Signature AH", offset: 2 },
  { label: "Depot Dossier", offset: 7 },
  { label: "Paiement", offset: 7 },
];

export function computeSteps(rawSteps, startDate) {
  let cumul = 0;
  const result = [];
  rawSteps.forEach(s => {
    cumul += s.offset;
    const d = new Date(startDate);
    d.setDate(d.getDate() + cumul);
    const last = result[result.length - 1];
    if (last && last.day === cumul) {
      last.labels.push(s.label);
    } else {
      result.push({ day: cumul, labels: [s.label], date: d });
    }
  });
  return result;
}

export function getSecondProgramSteps(rawSteps) {
  const filtered = rawSteps.filter(s => s.label !== "Rdv commercial");
  if (filtered.length > 0) filtered[0] = { ...filtered[0], offset: 0 };
  return filtered;
}

export function buildCashFlowEvents(programType, startDate, offsetDays, activeMPR, active174, regies, regieMode, isCum, isSecondCum = false) {
  const events = [];
  const today = new Date(startDate);

  if (programType === "mpr" && activeMPR) {
    const rawSteps = isSecondCum ? getSecondProgramSteps(MPR_TIMELINE_STEPS) : MPR_TIMELINE_STEPS;
    const charges = CHARGES_MPR_DEFAUT[activeMPR.type] || CHARGES_MPR_DEFAUT["PAC/SSC"];
    const regie = isCum ? regies.rMPRCum : regies.rMPR;
    const mprSteps = computeSteps(rawSteps, today);
    const rdvTravauxDay = mprSteps.find(s => s.labels.includes("RDV Travaux"))?.day || 16;
    const paiementDay = mprSteps[mprSteps.length - 1]?.day || 23;
    const caHT = activeMPR.result.marge + charges.marchandise + charges.sousTraitant + charges.admin + regie;

    events.push({ day: offsetDays + rdvTravauxDay + 15, label: "Sous-traitant MPR", amount: -charges.sousTraitant, prog: "MPR" });
    events.push({ day: offsetDays + 30, label: "Admin MPR", amount: -charges.admin, prog: "MPR" });
    events.push({ day: offsetDays + 45, label: "Marchandise MPR", amount: -charges.marchandise, prog: "MPR" });
    if (regieMode === "regie") {
      events.push({ day: offsetDays + paiementDay, label: "Regie MPR", amount: -regie, prog: "MPR" });
    } else {
      events.push({ day: offsetDays + 60, label: "Lead MPR", amount: -regie, prog: "MPR" });
    }
    events.push({ day: offsetDays + paiementDay, label: "Encaissement CA HT MPR", amount: caHT, prog: "MPR" });
  }

  if (programType === "174" && active174) {
    const rawSteps = isSecondCum ? getSecondProgramSteps(TIMELINE_174_STEPS) : TIMELINE_174_STEPS;
    const regie = isCum ? regies.r174Cum : regies.r174;
    const steps174 = computeSteps(rawSteps, today);
    const paiementDay = steps174[steps174.length - 1]?.day || 41;
    const coutHorsRegie = active174.result.coutTravaux - regie;
    const caHT = active174.result.caHT;

    events.push({ day: offsetDays + 30, label: "Cout travaux 174", amount: -coutHorsRegie, prog: "174" });
    if (regieMode === "regie") {
      events.push({ day: offsetDays + paiementDay, label: "Regie 174", amount: -regie, prog: "174" });
    } else {
      events.push({ day: offsetDays + 60, label: "Lead 174", amount: -regie, prog: "174" });
    }
    events.push({ day: offsetDays + paiementDay, label: "Encaissement CA HT 174", amount: caHT, prog: "174" });
  }

  return events.sort((a, b) => a.day - b.day);
}

// ═══ LABELS PARTAGES ═══
export const PROFIL_LABELS = { bailleur_sci: "Bailleur SCI", proprietaire_occupant: "Proprietaire occupant" };
export const PRECA_LABELS = { bleu: "Bleu (Tres Modeste)", jaune: "Jaune (Modeste)" };
export const GESTES_LABELS = { combles: "Isolation des combles", planchersBas: "Isolation du plancher bas", rampant: "Rampants", murs: "Isolation des murs interieurs", fenetres: "Fenetres" };
export const EQUIP_LABELS = { monoSplit: "Mono Split", vmcSimpleFlux: "VMC Simple flux", vmcDoubleFlux: "VMC Double flux", ballonThermo: "Ballon Thermodynamique" };

export const DOCS_PROFIL = {
  bailleur_sci: ["Statuts de la SCI", "Extrait Kbis (moins de 3 mois)", "Avis d'imposition de la SCI", "Justificatif de domicile", "Avis d'imposition du locataire", "Bail de location"],
  proprietaire_occupant: ["Justificatif de domicile", "Titre de propriete", "Avis d'imposition", "Taxe fonciere"]
};
export const DOCS_PROG = { "174": ["Audit energetique avant travaux", "DPE avant travaux", "Photos avant travaux"], MPR: ["Devis des travaux", "Documentation technique materiel", "Photos avant travaux"] };

export const STATUS_OPTS = [
  { value: "rdv_admin", label: "RDV Admin", color: "bg-blue-100 text-blue-700" },
  { value: "en_cours", label: "En cours", color: "bg-amber-100 text-amber-700" },
  { value: "valide", label: "Valide", color: "bg-emerald-100 text-emerald-700" },
  { value: "termine", label: "Termine", color: "bg-gray-100 text-gray-700" },
];

export const formatDateTime = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-";

export const REGIE_THRESHOLD = 4500;
